import React, { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import { FastField, Form, Formik } from 'formik';

import TireDetailMeta from './TireDetailMeta';
import ProductImages from '../ProductDetail/ProductImages';
import BreadCrumbSelector from '../../components/BreadCrumb/BreadCrumbSelector';
import { trackEvent } from '../../components/Tracking/Matomo';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import DealerInfo from '../../components/DealerInfo/DealerInfo';
import LegacyTireLabel from '../../components/Tires/LegacyTireLabel';

import { fetchWheelDetailIfNeeded } from '../../../modules/actions/standort/wheelDetail';
import { wheelDetailResponseSelector } from '../../../modules/selectors/standort/wheelDetail';
import Currency from '../../../helpers/currency';
import { taxLabel } from '../../../helpers/taxHelper';
import { speedIndexToSpeed } from '../../../helpers/tiresHelper';
import Meisterkachel from '../../components/Meisterkachel/Meisterkachel';
import Button from '../../components/form-elements/Button';
import TireInformationModal from '../../components/Tires/TireInformationModal';
import TireEnergyLabelLinks from '../Tires/TireEnergyLabelLinks';
import Anchor from '../../components/form-elements/Anchor';
import withStandortRouter from '../../components/VHost/withStandortRouter';
import { InternalLink } from '../../components/form-elements/Link';
import { addReifen } from '../../../modules/actions/standort/session/cart';
import { markenStandortSelector } from '../../../modules/selectors/standort/markenStandort';
import { useHistory } from 'react-router-dom';
import { saveproduct, resetproduct } from '../../../modules/actions/productID';

// TODO: merge/refactor with ProductDetail QuantityBox
function QuantityBox({ tire, online: isOnline, lieferweg, price }) {
    const dispatch = useDispatch();

    function handleSubmit({ quantity }) {
        trackEvent({
            category: 'E-Commerce',
            action: 'Lege-in-Warenkorb',
            label: tire.neutralKey,
            value: price * quantity * 100,
        });
        dispatch(
            addReifen({
                reifen: {
                    ...tire,
                    selectedLieferweg: lieferweg,
                    selectedBezugsquelle: {
                        ...tire.selectedBezugsquelle,
                        vk_preis: price,
                    },
                },
                quantity,
            })
        );
    }

    return (
        <Formik
            initialValues={{ quantity: 1 }}
            onSubmit={handleSubmit}
            render={({ values }) => (
                <Form>
                    <div className="uk-background-muted uk-padding" data-uk-margin="">
                        <div className="uk-grid-small uk-flex-middle" data-uk-grid="">
                            <div className="uk-width-1-1 uk-width-1-3@l">
                                <div className="uk-text-center">
                                    <span className="aps-price">
                                        <Currency quantity={price * values.quantity} />
                                    </span>
                                    <div className="uk-text-small uk-text-muted">
                                        <div>Preis inkl. {taxLabel()} MwSt.</div>
                                        {isOnline && (
                                            <div>
                                                zzgl. etwaiger <InternalLink to="/versandkosten" ariaLabel="Informationen zu Versandkosten"><u>Versandkosten</u></InternalLink>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="uk-width-1-1 uk-width-1-3@l">
                                <div className="uk-flex uk-flex-column uk-flex-middle uk-flex-left@l">
                                    <div className="uk-grid-small uk-flex-middle" data-uk-grid="">
                                        <label className="uk-form-label" htmlFor="quantity">
                                            Menge
                                        </label>
                                        <div className="uk-form-controls">
                                            <FastField className="uk-input" name="quantity" type="number" min={1} max={1000} aria-label="Menge festlegen, Minimum: 1 Maximum: 1000" id="quantity"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="uk-width-1-1 uk-width-1-3@l uk-flex uk-flex-center">
                                <div>
                                    <Button type="submit" ariaLabel={isOnline ? 'Zum Warenkorb hinzufügen' : 'RESERVIEREN & ABHOLEN'}>{isOnline ? 'Zum Warenkorb hinzufügen' : 'RESERVIEREN & ABHOLEN'}</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        />
    );
}

function TireDetail({ tireDetail, addReifen }) {
    const markenStandort = useSelector(markenStandortSelector);
    const dispatch = useDispatch();
    const history = useHistory();
    const additionalCrumbs = [
        { url: `/reifen-felgen`, label: 'Reifen & Felgen' },
        {
            url: `/${tireDetail.saison.toLowerCase()}reifen`,
            label: `${tireDetail.saison}reifen`,
        },
        {
            url: `/reifen/${tireDetail.fahrzeugtyp}/${tireDetail.saison}/${tireDetail.breite}/${tireDetail.querschnitt}/${tireDetail.durchmesser}`,
            label: 'Reifenauswahl',
        },
        {
            label: 'Reifen',
        },
    ];

    const additionalInformation = tireDetail?.additionalInfo?.dotWare
        ? { label: `DOT: ${tireDetail.additionalInfo.dotWare}`, tooltip: 'DOT = Herstellungsjahr des Reifens' }
        : tireDetail?.additionalInfo?.herstellerGarantie
            ? { label: 'mit Herstellergarantie', tooltip: 'Spezielle Garantie des Herstellers' }
            : tireDetail?.additionalInfo?.demo
                ? {
                    label: 'DEMO-Reifen',
                    tooltip:
                        'Demontierte Reifen bis Max. 50km Laufleistung von Vorführ- oder Neufahrzeugen bzw. von Kompletträdern zur Winter/Sommer Saison',
                }
                : undefined;
    const vorteile = tireDetail?.onlineLieferweg?.Vorteilskommunikation ?? tireDetail?.pickupLieferweg?.Vorteilskommunikation ?? [];

    useEffect(() => {
        if (tireDetail) {
            let productID;
            productID = tireDetail.selectedBezugsquelle.artNummer;
            dispatch(saveproduct(productID));
        }
    }, [dispatch, tireDetail]);

    useEffect(() => {
        const unlisten = history.listen((location, action) => {
            // ... logic
            dispatch(resetproduct());
        });

        return unlisten;
    }, [dispatch, history]);

    return (
        <>
            <TireDetailMeta tireDetail={tireDetail} />
            <BreadCrumbSelector additionalCrumbs={additionalCrumbs} />
            <section className="uk-section uk-section-default uk-section-small">
                <div className="uk-container uk-margin-medium-top">
                    <h1 className="uk-margin-remove">
                        {tireDetail.hersteller} {tireDetail.profil} <span className="uk-text-muted">{tireDetail.bezeichnung}</span>
                    </h1>
                    <h2 className="uk-text-light uk-margin-remove">{tireDetail.warengruppe}</h2>
                    <p>Artikelnummer: {tireDetail.selectedBezugsquelle.artNummer}</p>
                    <div className="uk-grid-small uk-child-width-1-2@s" data-uk-grid="">
                        <ProductImages variant={tireDetail} images={tireDetail.images || []} />
                        <div>
                            <div className="uk-child-width-1-1 uk-grid-small" data-uk-grid="">
                                {tireDetail.onlineLieferweg && (
                                    <QuantityBox
                                        addReifen={addReifen}
                                        tire={tireDetail}
                                        price={tireDetail.selectedBezugsquelle.onlineVK}
                                        lieferweg={tireDetail.onlineLieferweg}
                                        online
                                    />
                                )}
                                {tireDetail.pickupLieferweg && !markenStandort.isTechno && (
                                    <QuantityBox
                                        addReifen={addReifen}
                                        tire={tireDetail}
                                        price={tireDetail.selectedBezugsquelle.vk_preis}
                                        lieferweg={tireDetail.pickupLieferweg}
                                    />
                                )}
                                {tireDetail.energyLabelUrl && (
                                    <div>
                                        <div className="uk-margin-small-bottom">
                                            <TireInformationModal tire={tireDetail} />
                                        </div>
                                        <div data-uk-grid="" className="uk-grid-small uk-child-width-expand@s">
                                            <TireEnergyLabelLinks tire={tireDetail} />
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <table className="uk-table uk-table-striped uk-table-small">
                                        <tbody>
                                            <tr>
                                                <td>Hersteller</td>
                                                <td>{tireDetail.hersteller}</td>
                                            </tr>
                                            <tr>
                                                <td>Profil</td>
                                                <td>{tireDetail.profil}</td>
                                            </tr>
                                            <tr>
                                                <td>Größe</td>
                                                <td>{tireDetail.bezeichnung}</td>
                                            </tr>
                                            <tr>
                                                <td>Geschwindigkeitsindex</td>
                                                <td>
                                                    {tireDetail.speedindex} bis {speedIndexToSpeed[tireDetail.speedindex]} km/h
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Loadindex</td>
                                                <td>{tireDetail.loadindex}</td>
                                            </tr>
                                            <tr>
                                                <td>Kategorie</td>
                                                <td>{tireDetail.warengruppe}</td>
                                            </tr>
                                            <tr>
                                                <td>Zusatzinformationen</td>
                                                <td>
                                                    {additionalInformation && (
                                                        <div data-uk-tooltip={additionalInformation.tooltip}>
                                                            <i className="fa fa-info-circle" aria-hidden="true" />{' '}
                                                            {additionalInformation.label}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>DEMO-Reifen</td>
                                                <td>
                                                    {tireDetail?.additionalInfo?.demo ? 'Ja' : 'Nein'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p className="uk-text-muted">Lieferung ohne Felge</p>
                                </div>
                                <div>
                                    {!tireDetail.energyLabelUrl && (
                                        <ul data-uk-accordion="">
                                            <li className="uk-open">
                                                <Anchor className="uk-accordion-title">EU-Reifenlabel</Anchor>
                                                <div className="uk-accordion-content">
                                                    <LegacyTireLabel tireDetails={tireDetail} />
                                                </div>
                                            </li>
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {vorteile.length > 0 && (
                <section className="uk-section uk-section-muted uk-section-small">
                    <div className="uk-container">
                        <div className="uk-flex uk-flex-wrap uk-flex-center@m">
                            {vorteile.map((vorteil, i) => {
                                if (typeof vorteil === 'object') {
                                    return (
                                        <div className="uk-flex uk-flex-middle uk-margin-small-right uk-width-1-1 uk-width-auto@m" key={i}>
                                            <div
                                                className="uk-margin-small-right uk-font-main"
                                                dangerouslySetInnerHTML={{ __html: vorteil.path }}
                                            />
                                            {vorteil.text}
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div className="uk-flex uk-flex-middle uk-margin-small-right uk-width-1-1 uk-width-auto@m" key={i}>
                                            <i className="fa fa-check uk-margin-small-right uk-font-main" />
                                            {vorteil}
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    </div>
                </section>
            )}
            <section className="uk-section uk-section-muted uk-section-small">
                <div className="uk-container uk-container-xsmall">
                    <Meisterkachel />
                </div>
            </section>
            <DealerInfo />
        </>
    );
}

function LoadTireDetail({ wheelDetailResponse }) {
    return <ReduxSuspense response={wheelDetailResponse}>{tireDetail => <TireDetail tireDetail={tireDetail} />}</ReduxSuspense>;
}

const frontload = async props => {
    const { fetchWheelDetailIfNeeded, wheelId } = props;
    await fetchWheelDetailIfNeeded({ wheelId });
};

const mapDispatchToProps = { fetchWheelDetailIfNeeded };

const mapStateToProps = (
    state,
    {
        match: {
            params: { id: wheelId },
        },
    }
) => {
    return { wheelId, wheelDetailResponse: wheelDetailResponseSelector(state, { wheelId }) };
};

export default withStandortRouter(
    connect(mapStateToProps, mapDispatchToProps)(frontloadConnect(frontload, { onUpdate: true })(LoadTireDetail))
);
